import { useEffect, useRef, useState } from "react";
import type {
  ActionDecision,
  CannedScenario,
  ExecutionResult,
} from "../data/types";

interface Props {
  scenario: CannedScenario;
  decisions: Map<string, ActionDecision>;
  results: Map<string, ExecutionResult>;
  setResults: (
    updater:
      | Map<string, ExecutionResult>
      | ((prev: Map<string, ExecutionResult>) => Map<string, ExecutionResult>),
  ) => void;
  onComplete: () => void;
}

// Simulated execution outputs per action_type
function executionOutput(actionType: string, target: string): string {
  const map: Record<string, string> = {
    capture_memory: `2.4 GB memory dump uploaded to r2://aria-audit/${target}/memory.bin`,
    isolate_host: `${target} isolated at 14:38:21 via CrowdStrike RTR`,
    revoke_sessions: `7 active sessions revoked for ${target}`,
    force_password_reset: `Password reset enforced for ${target}; webauthn re-enrollment required`,
    delete_mailbox_rule: `Forwarding rule removed; 0 messages intercepted`,
    block_c2: `${target} blocked at Cloudflare Gateway (TLS + DNS)`,
    block_domain: `${target} blocked at Email Gateway + Web Gateway`,
    block_source_ip: `${target} blocked at IAM Gateway`,
    block_destination: `${target} blocked fleet-wide at Cloudflare Gateway`,
    notify_user: `Direct chat sent to ${target}`,
    notify_manager: `Manager notified via email`,
    disable_account: `Account ${target} disabled in Authentik; admin re-enable required`,
    revoke_all_app_passwords: `All app passwords revoked for ${target}`,
    audit_privileged_actions: `Audit query returned 0 actions in the suspect window`,
    preserve_evidence: `Evidence preserved in r2://aria-audit/forensics/`,
    snapshot_drives: `Full-disk snapshot complete: 487 GB`,
    restrict_access: `Access scope reduced to read-only on production systems`,
    legal_hold: `Legal hold placed; notification sent to Privacy team`,
    defer_user_notification: `User notification deferred per Legal direction`,
  };
  return map[actionType] ?? `Action ${actionType} executed against ${target}`;
}

// Per-action-type execution duration (ms)
function executionDurationMs(actionType: string): number {
  if (actionType.startsWith("capture_") || actionType.startsWith("snapshot_")) return 2800;
  if (actionType.startsWith("notify_")) return 600;
  if (actionType.startsWith("block_")) return 1200;
  if (actionType === "legal_hold" || actionType === "defer_user_notification") return 400;
  return 1800;
}

// Plain-English summary of what an action *does* — what the analyst would
// tell a peer in one breath. The technical slug, action ID, and integration
// only show when the row is expanded.
function plainSummary(actionType: string, target: string): string {
  const t = target;
  const map: Record<string, string> = {
    // Forensics & evidence
    capture_memory:           `Capture a memory snapshot from ${t}`,
    snapshot_drives:          `Take a full-disk snapshot of ${t}`,
    capture_inference_logs:   `Capture the AI agent's inference logs for ${t}`,
    preserve_evidence:        `Preserve evidence to the forensics bucket`,
    pull_access_logs:         `Pull access logs for ${t}`,

    // Containment — host
    isolate_host:             `Isolate ${t} from the network`,
    confirm_isolation:        `Confirm ${t} is still isolated`,
    pause_agent:              `Pause the AI agent ${t}`,
    pause_ci_fleet:           `Pause the CI/CD fleet`,

    // Containment — identity
    revoke_sessions:          `Revoke all active sessions for ${t}`,
    force_password_reset:     `Force a password reset for ${t}`,
    force_password_reset_batch: `Force password resets across a batch of users`,
    disable_account:          `Disable the account ${t}`,
    revoke_all_app_passwords: `Revoke every app password for ${t}`,
    restrict_access:          `Restrict ${t} to read-only on production`,
    rotate_service_credential: `Rotate the service credential for ${t}`,
    rotate_api_key:           `Rotate the API key for ${t}`,
    rotate_ci_credential:     `Rotate the CI credential for ${t}`,
    rotate_ci_secrets:        `Rotate every CI secret`,

    // Containment — network
    block_c2:                 `Block the C2 destination ${t}`,
    block_domain:             `Block the domain ${t}`,
    block_source_ip:          `Block the source IP ${t}`,
    block_destination:        `Block ${t} fleet-wide at the Cloudflare Gateway`,
    block_asn_temporarily:    `Temporarily block traffic from ASN ${t}`,
    enable_managed_challenge: `Turn on Cloudflare's managed challenge for ${t}`,
    rate_limit_endpoint:      `Rate-limit the endpoint ${t}`,

    // Email
    delete_mailbox_rule:      `Delete the malicious mailbox rule on ${t}`,
    purge_email_from_inboxes: `Purge the malicious email from every inbox`,

    // Recovery
    initiate_restore_from_backup: `Restore ${t} from the last clean backup`,
    revert_bucket_acl:        `Revert the bucket ACL for ${t}`,
    pin_dependency_version:   `Pin ${t} to a known-good version`,

    // Notifications
    notify_user:              `Notify ${t}`,
    notify_manager:           `Notify the manager`,
    notify_ir_lead_and_eisg:  `Page the IR lead and the EISG (CSO / CPO / CLO)`,
    notify_reporter:          `Thank the reporter and close the loop`,
    notify_privacy_team:      `Notify the privacy team`,
    notify_security_awareness: `Notify the security awareness team`,
    notify_engineering_leadership: `Notify engineering leadership`,
    notify_affected_customers: `Notify affected customers`,

    // Investigation
    audit_privileged_actions: `Audit ${t}'s privileged actions in the suspect window`,
    audit_iam_actor:          `Audit everything ${t} touched`,
    audit_ci_jobs:            `Audit recent CI/CD jobs`,
    threat_hunt_lateral:      `Threat-hunt for lateral movement around ${t}`,
    identify_exposed_records: `Identify which records were exposed`,

    // Legal
    legal_hold:               `Place a legal hold`,
    defer_user_notification:  `Defer notifying the user pending Legal review`,
  };
  return map[actionType] ?? `${humanize(actionType)} on ${t}`;
}

function humanize(slug: string): string {
  const s = slug.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusLine(status: string, integration: string): string {
  switch (status) {
    case "pending":     return "Waiting in queue";
    case "dispatching": return `Dispatching to ${integration}`;
    case "executing":   return `Running on ${integration}`;
    case "done":        return "Completed successfully";
    case "failed":      return "Failed";
    case "skipped":     return "Skipped (not approved by analyst)";
    default:            return "";
  }
}

export function Execution({
  scenario,
  decisions,
  results,
  setResults,
  onComplete,
}: Props) {
  const startedRef = useRef(false);
  const setResultsRef = useRef(setResults);
  setResultsRef.current = setResults;

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Walk through approved actions in order, respecting prerequisites
    const approved = scenario.action_plan.actions.filter(
      (a) => decisions.get(a.action_id)?.decision === "approve",
    );
    const setResults = setResultsRef.current;

    const totalDelay = 400;
    let cumulative = totalDelay;
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (const action of approved) {
      const dispatchAt = cumulative;
      const execDuration = executionDurationMs(action.action_type);
      const doneAt = dispatchAt + 600 + execDuration;
      cumulative = doneAt + 200;

      // Dispatch phase
      timers.push(
        setTimeout(() => {
          setResults((prev) => {
            const next = new Map(prev);
            next.set(action.action_id, {
              action_id: action.action_id,
              status: "dispatching",
            });
            return next;
          });
        }, dispatchAt),
      );

      // Execute phase
      timers.push(
        setTimeout(() => {
          setResults((prev) => {
            const next = new Map(prev);
            next.set(action.action_id, {
              action_id: action.action_id,
              status: "executing",
            });
            return next;
          });
        }, dispatchAt + 400),
      );

      // Done phase
      timers.push(
        setTimeout(() => {
          setResults((prev) => {
            const next = new Map(prev);
            next.set(action.action_id, {
              action_id: action.action_id,
              status: "done",
              duration_ms: execDuration,
              output: executionOutput(action.action_type, action.target),
            });
            return next;
          });
        }, doneAt),
      );
    }

    // After all done, enable Resolution
    timers.push(
      setTimeout(() => {
        // No-op; the button enables based on results state
      }, cumulative),
    );

    return () => {
      for (const t of timers) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [expanded, setExpanded] = useState<string | null>(null);

  const allFinal = scenario.action_plan.actions.every((a) => {
    const r = results.get(a.action_id);
    return r && (r.status === "done" || r.status === "failed" || r.status === "skipped");
  });

  return (
    <div>
      <div className="queue-header">
        <h1>Running the response</h1>
        <p>
          Each approved action is being carried out against the right system.
          Click any row for the technical details.
        </p>
      </div>

      <div className="execution-list">
        {scenario.action_plan.actions.map((a) => {
          const result = results.get(a.action_id);
          const status = result?.status ?? "pending";
          const symbol =
            status === "done"
              ? "✓"
              : status === "failed"
                ? "✗"
                : status === "executing"
                  ? "●"
                  : status === "dispatching"
                    ? "▸"
                    : status === "skipped"
                      ? "—"
                      : "·";
          const isExpanded = expanded === a.action_id;
          const summary = plainSummary(a.action_type, a.target);
          const line = statusLine(status, a.integration);

          return (
            <div key={a.action_id} className={`exec-card ${isExpanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="exec-card-head"
                onClick={() => setExpanded(isExpanded ? null : a.action_id)}
                aria-expanded={isExpanded}
              >
                <span className={`exec-status-icon ${status}`}>{symbol}</span>
                <div className="exec-card-body">
                  <div className="exec-summary">{summary}</div>
                  <div className="exec-status-line">{line}</div>
                </div>
                <div className="exec-card-meta">
                  {status === "done" && result?.duration_ms != null && (
                    <span className="exec-duration">
                      {(result.duration_ms / 1000).toFixed(1)}s
                    </span>
                  )}
                  {status === "executing" && (
                    <span className="exec-duration exec-duration-live">running</span>
                  )}
                  <span className={`exec-chevron ${isExpanded ? "open" : ""}`}>›</span>
                </div>
              </button>

              {isExpanded && (
                <div className="exec-card-detail">
                  <DetailRow label="Integration"    value={a.integration} />
                  <DetailRow label="Action ID"      value={a.action_id} mono />
                  <DetailRow label="Action type"    value={a.action_type} mono />
                  <DetailRow label="Target"         value={a.target} mono />
                  {status === "done" && result?.output && (
                    <DetailRow label="Execution output" value={result.output} mono block />
                  )}
                  {status === "done" && result?.duration_ms != null && (
                    <DetailRow label="Duration" value={`${result.duration_ms} ms`} mono />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="triage-flight-actions">
        <button
          type="button"
          className="button primary"
          onClick={onComplete}
          disabled={!allFinal}
        >
          {allFinal ? "View resolution →" : "Working through the actions…"}
        </button>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  block,
}: {
  label: string;
  value: string;
  mono?: boolean;
  block?: boolean;
}) {
  return (
    <div className={`exec-detail-row ${block ? "block" : ""}`}>
      <div className="exec-detail-label">{label}</div>
      <div className={`exec-detail-value ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}
